/* eslint-disable max-len */
/* eslint-disable indent */
import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  DialogContent,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DOMPurify from 'dompurify';

import { AuthContext } from 'utils/AuthContext';
import { saveInsuranceDetails, getInsuranceDetails, getPatientDetails, deleteInsurance } from 'store/slices/userSlice';
import FileUploader from './FileUploader';
import { useAppDispatch } from 'store/hooks';
import { insuranceDetailsSchema } from 'utils/schema';
import PrimaryCard from './PrimaryCard';
import { uploadCardImageAPI, downloadCardImageAPI } from 'services/profileServices';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { permissions } from 'utils/dropDownList';
import { Restricted } from 'components/Restricted';
import { validateZipcode } from 'store/slices/signInSlice';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { useLocation } from 'react-router-dom';
import { getAddCardLink } from 'store/slices/appointmentSlice';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { StyledDialog } from 'theme/index';
import { validatePaymentStatus } from 'store/slices/patientInfoSlice';

const BulletItem = ({ children }: { children: React.ReactNode }) => (
  <Stack direction='row' alignItems='center' spacing={1}>
    <Box
      sx={{
        width: 4,
        height: 4,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        flexShrink: 0,
      }}
    />
    <Typography variant='body2'>{children}</Typography>
  </Stack>
);

const InsuranceDetails = (props: any) => {
  const [primaryInsurance, setPrimaryInsurance] = useState(false);
  const [secondaryInsurance, setSecondaryInsurance] = useState(false);
  const [primaryInsuranceData, setPrimaryInsuranceData] = useState<any>(null);
  const [secondaryInsuranceData, setSecondaryInsuranceData] = useState<any>(null);
  //Place holder frontSideImage, backSideImage used only for upload
  const [frontSideImage, setFrontSideImage] = useState<string | null>(null);
  const [backSideImage, setBackSideImage] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [payOutOfPocket, setPayOutOfPocket] = useState<boolean>(false);
  // Separate states to hold front and back images for primary when editing, so that they don't get overridden when user switch between primary and secondary insurance
  const [primaryFrontSideImage, setPrimaryFrontSideImage] = useState<any>(null);
  const [primaryBackSideImage, setPrimaryBackSideImage] = useState<any>(null);
  // Separate states to hold front and back images for secondary when editing, so that they don't get overridden when user switch between primary and secondary insurance
  const [secondaryFrontSideImage, setSecondaryFrontSideImage] = useState<any>(null);
  const [secondaryBackSideImage, setSecondaryBackSideImage] = useState<any>(null);

  const [showExplanation, setShowExplanation] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState(0);
  const [insuranceProviders, setInsuranceProviders] = useState<any>([]);
  const [displayInsuranceMessage, setDisplayInsuranceMessage] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  //used for progress bar for card image upload
  const [backCardUploadProgress, setBackCardUploadProgress] = useState<number>(0);
  const [frontCardUploadProgress, setFrontCardUploadProgress] = useState<number>(0);
  const [disableDelete, setDisableDelete] = useState<boolean>(true);
  const [paymentId, setPaymentId] = useState<number>(0);
  const [userId, setUserId] = useState<number>(0);

  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const { state } = useLocation();
  const { user } = useContext(AuthContext);
  const isAdmin = user.isAdmin;
  const launchFromPatientSummery = props?.launchFromPatientSummery ? true : false;
  const patientId = isAdmin ? state?.patientId ?? props?.patientId : user?.patientId;
  const canEdit = useMemo(() => user?.permissions.includes('EDIT_PROFILE'), [user?.permissions]);
  const [deleteType, setDeleteType] = useState<'PRIMARY' | 'SECONDARY' | null>(null);
  const [creditCardStatus, setCreditCardStatus] = useState<string>('');
  const [showPaymentConfirm, setShowPaymentConfirm] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(insuranceDetailsSchema),
    mode: 'all',
  });

  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedProviderDescription, setSelectedProviderDescription] = useState<string>('');
  useEffect(() => {
    if (selectedProvider && insuranceProviders?.length > 0) {
      const provider = insuranceProviders.find((p: any) => p.name === selectedProvider);
      setSelectedProviderDescription(provider ? provider.description : '');
    }
  }, [selectedProvider, insuranceProviders]);

  const applyInsuranceFieldValues = (insurance: Record<string, any>, shouldValidate = true) => {
    Object.keys(insurance).forEach((key: any) => {
      setValue(key, insurance[key], {
        shouldValidate,
        shouldDirty: false,
      });
    });
  };

  const loadThubmnails = async () => {
    try {
      setBackSideImage(null);
      setFrontSideImage(null);
      setLoading(true);
      if (patientId) {
        const response = await dispatch(getInsuranceDetails(patientId)).unwrap();
        if (response.status === 200) {
          setPayOutOfPocket(response?.data?.payOutOfPocket);
          setUserId(response?.data?.userId || null);
          setPaymentId(response?.data?.id);
          if (response?.data?.payOutOfPocket) {
            validatePayment();
            setShowExplanation(true);
          } else {
            validateInsuranceAndZipcode();
            if (response?.data?.primaryInsurance) {
              if (response?.data?.secondaryInsurance === null) {
                applyInsuranceFieldValues(response.data.primaryInsurance);
              }
              setSelectedProvider(response?.data?.primaryInsurance?.insuranceCarrier || '');
              setPrimaryInsurance(true);
              setShowForm(false);
              setPrimaryInsuranceData(response?.data?.primaryInsurance);
              setActiveStep(0);
            } else {
              setShowForm(true);
              setValue('insuranceCarrier', user?.insurance, {
                shouldValidate: true,
                shouldDirty: false,
              });
            }
            if (response?.data?.secondaryInsurance) {
              applyInsuranceFieldValues(response.data.secondaryInsurance);

              setSelectedProvider(response?.data?.secondaryInsurance?.insuranceCarrier || '');
              setSecondaryInsurance(true);
              setDisableDelete(false);
              setSecondaryInsuranceData(response?.data?.secondaryInsurance);
            }
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const validateInsuranceAndZipcode = async () => {
    try {
      setLoading(true);
      dispatch(getPatientDetails(patientId))
        .unwrap()
        .then((response: any) => {
          if (response?.status === 200) {
            if (response?.data?.invalidInsurance) {
              setDisplayInsuranceMessage(true);
            }
            const zip = response?.data?.zipcode ? parseInt(response?.data?.zipcode) : parseInt('00000');
            dispatch(validateZipcode(zip))
              .unwrap()
              .then((response: any) => {
                setInsuranceProviders(response?.data?.insurance);
              });
          }
        });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadThubmnails();
  }, [patientId]);

  useEffect(() => {
    if (showForm) {
      reset({
        groupNo: '',
        insuranceCarrier: user?.insurance,
        patientRelation: '',
        planId: '',
        subscriberDob: '',
        subscriberName: '',
      });
    }
  }, [reset, showForm, user?.insurance]);

  const onSubmitForm = async (formData: any) => {
    try {
      if (isValid && isDirty) {
        if (!payOutOfPocket && (!frontSideImage || !backSideImage)) {
          toast('Please upload both the front and back of your insurance card.', {
            type: 'error',
          });
          return;
        }
        setLoading(true);
        if (patientId) {
          delete formData.id;
          const data = secondaryInsurance
            ? {
                secondaryInsurance: {
                  ...formData,
                },
                id: patientId,
              }
            : {
                primaryInsurance: {
                  ...formData,
                },
                id: patientId,
              };

          let cardUploadFailed = false;

          const reportCardUploadFailure = (error: any, side: 'front' | 'back') => {
            cardUploadFailed = true;
            const serverMessage = error?.response?.data?.errorMsg;
            toast(serverMessage ?? `Failed to upload the ${side} side of your insurance card. Please try again.`, {
              type: 'error',
            });
          };

          await dispatch(saveInsuranceDetails(data))
            .unwrap()
            .then(async (response: any) => {
              if (response?.status === 200) {
                if (frontSideImage !== null) {
                  const frontSideFormData: any = new FormData();
                  frontSideFormData.append('file', frontSideImage);
                  const frontSideData = {
                    params: {
                      patientId: patientId,
                      imageSide: 'Front',
                      insuranceType: secondaryInsurance ? 'Secondary' : 'Primary',
                    },
                    formData: frontSideFormData,
                  };
                  try {
                    await uploadCardImageAPI(
                      (progress) => setFrontCardUploadProgress(progress),
                      frontSideData.params,
                      frontSideData.formData
                    );
                  } catch (error) {
                    reportCardUploadFailure(error, 'front');
                    return;
                  }
                }
                if (backSideImage !== null) {
                  const backsideFormData: any = new FormData();
                  backsideFormData.append('file', backSideImage);
                  const backSideData = {
                    params: {
                      patientId: patientId,
                      imageSide: 'Back',
                      insuranceType: secondaryInsurance ? 'Secondary' : 'Primary',
                    },
                    formData: backsideFormData,
                  };
                  try {
                    await uploadCardImageAPI(
                      (progress) => setBackCardUploadProgress(progress),
                      backSideData.params,
                      backSideData.formData
                    );
                  } catch (error) {
                    reportCardUploadFailure(error, 'back');
                    return;
                  }
                }
              }
            });

          if (cardUploadFailed) {
            return;
          }

          toast('Payment details have been saved successfully', {
            type: 'success',
            icon: true,
          });

          reset(formData);
          if (!payOutOfPocket) {
            loadThubmnails();
          }
          if (props?.onComplete) {
            props.onComplete();
          }
        }
      } else {
        toast('No changes to update.', {
          type: 'info',
          icon: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (acceptedFiles: any, side: string) => {
    if (acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];
      const reader = new FileReader();
      reader.onload = () => {
        if (side === 'front') {
          setFrontSideImage(file);
        } else {
          setBackSideImage(file);
        }
      };
      setValue('cardImage', 'file', { shouldValidate: true, shouldDirty: true });
      register('cardImage');
      reader.readAsDataURL(file);
    }
  };

  const resetPrimaryInsurance = () => {
    setPrimaryInsuranceData(null);
    setSelectedProviderDescription('');
    setSelectedProvider('');
    setPrimaryFrontSideImage(null);
    setPrimaryBackSideImage(null);
    setBackSideImage(null);
    setFrontSideImage(null);
    setSecondaryInsurance(false);
    setLoading(true);
    setShowForm(true);
  };
  const editPrimaryInsurance = async () => {
    try {
      resetPrimaryInsurance();
      dispatch(getInsuranceDetails(patientId))
        .unwrap()
        .then((response: any) => {
          if (response.status === 200) {
            if (response?.data?.primaryInsurance) {
              applyInsuranceFieldValues(response.data.primaryInsurance, false);
              setPrimaryInsurance(true);
              setPrimaryInsuranceData(response?.data?.primaryInsurance);
              setSelectedProvider(response?.data?.primaryInsurance?.insuranceCarrier || '');
            }
          }
        });

      const frontSideData = {
        params: {
          patientId,
          imageSide: 'Front',
          insuranceType: 'Primary',
        },
      };
      await downloadCardImageAPI(frontSideData.params).then((response: any) => {
        if (response?.status === 200 && response?.data !== '') {
          setPrimaryFrontSideImage(response?.data);
        }
      });
      const backSideData = {
        params: {
          patientId,
          imageSide: 'Back',
          insuranceType: 'Primary',
        },
      };
      await downloadCardImageAPI(backSideData.params).then((response: any) => {
        if (response?.status === 200 && response?.data) {
          setPrimaryBackSideImage(response?.data);
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const resetSecondaryInsurance = () => {
    setSecondaryFrontSideImage(null);
    setSecondaryBackSideImage(null);
    setSelectedProviderDescription('');
    setSelectedProvider('');
    setSecondaryInsuranceData(null);
    setBackSideImage(null);
    setFrontSideImage(null);
    setPrimaryInsurance(false);
    setLoading(true);
    setShowForm(true);
  };
  const editSecondaryInsurance = async () => {
    try {
      resetSecondaryInsurance();
      dispatch(getInsuranceDetails(patientId))
        .unwrap()
        .then((response: any) => {
          if (response.status === 200) {
            if (response?.data?.secondaryInsurance) {
              applyInsuranceFieldValues(response.data.secondaryInsurance);
              setSecondaryInsurance(true);
              setSecondaryInsuranceData(response?.data?.secondaryInsurance);
              setSelectedProvider(response?.data?.secondaryInsurance?.insuranceCarrier || '');
            }
          }
        });

      const frontSideData = {
        params: {
          patientId: patientId,
          imageSide: 'Front',
          insuranceType: 'Secondary',
        },
      };
      downloadCardImageAPI(frontSideData.params).then((response: any) => {
        if (response?.status === 200 && response?.data) {
          setSecondaryFrontSideImage(response?.data);
        }
      });

      const backSideData = {
        params: {
          patientId,
          imageSide: 'Back',
          insuranceType: 'Secondary',
        },
      };
      downloadCardImageAPI(backSideData.params).then((response: any) => {
        if (response?.status === 200 && response?.data) {
          setSecondaryBackSideImage(response?.data);
        }
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteInsurance = async () => {
    if (!patientId || !deleteType) return;
    try {
      setLoading(true);
      const insuranceDetails = {
        paymentInfoId: paymentId,
        patientId,
      };

      if (deleteType === 'SECONDARY') {
        await dispatch(deleteInsurance(insuranceDetails)).unwrap();
      }
      toast('Insurance deleted successfully', { type: 'success' });
    } catch (error) {
      toast('Failed to delete insurance', { type: 'error' });
    } finally {
      setSecondaryInsuranceData(null);
      setSecondaryInsurance(false);
      setSecondaryBackSideImage(null);
      setSecondaryFrontSideImage(null);
      setShowConfirm(false);
      setDeleteType(null);
      setLoading(false);
    }
  };

  const redirectPaymentMethod = () => {
    if (showPaymentConfirm) {
      handleViewCreditCard();
      setShowPaymentConfirm(false);
    }
  };

  const handleViewCreditCard = () => {
    dispatch(getAddCardLink({}))
      .unwrap()
      .then((response: any) => {
        if (response.status === 200) {
          setTimeout(() => {
            window.open(response?.data?.url, '_blank');
          });
        }
      });
  };
  const validatePayment = async () => {
    const id = isAdmin ? userId : user?.userId;
    if (isAdmin && !userId) return;
    setLoading(true);
    dispatch(validatePaymentStatus(id))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const data = response?.data;
          const status = data?.status;
          const expired = data?.cardExpired;
          if (status === 'No') {
            setCreditCardStatus('No Payment method on file');
          } else if (status === 'Yes' && !expired) {
            setCreditCardStatus('Payment method on file');
          } else if (status === 'Yes' && expired) {
            setCreditCardStatus('Payment method on file has expired');
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePayOutOfPocketChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!patientId) return;
    const checked = e.target.checked;
    try {
      setLoading(true);
      setShowForm(false);
      // Update UI immediately
      setPayOutOfPocket(checked);
      setShowExplanation(checked);

      const response = await dispatch(
        saveInsuranceDetails({
          id: patientId,
          payOutOfPocket: checked, // ✅ use checked, NOT state
        })
      ).unwrap();

      if (response.status === 200) {
        if (checked) {
          await validatePayment();
        } else {
          setLoading(false);
          await loadThubmnails();
        }
      }
    } catch (error) {
      toast('Payment changes not saved', {
        type: 'error',
      });

      // Rollback UI if API fails
      setPayOutOfPocket(!checked);
      setShowExplanation(!checked);
      setShowForm(checked);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (val: any) => {
    if (val) {
      setValue('subscriberDob', dayjs(val).format('MM/DD/YYYY'), {
        shouldValidate: true,
        shouldDirty: true,
      });
    } else {
      setValue('subscriberDob', '', {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };
  const isSm = useMediaQuery('(max-width:786px)');
  const cardImageStyle = {
    maxWidth: isSm ? '245px' : '300px',
    maxHeight: isSm ? '245px' : '385px',
  };

  let frontCardImageSrc: string | null = null;
  if (secondaryInsurance && secondaryFrontSideImage) {
    frontCardImageSrc = secondaryFrontSideImage;
  } else if (primaryInsurance && primaryFrontSideImage) {
    frontCardImageSrc = primaryFrontSideImage;
  }

  let backCardImageSrc: string | null = null;
  if (secondaryInsurance && secondaryBackSideImage) {
    backCardImageSrc = secondaryBackSideImage;
  } else if (primaryInsurance && primaryBackSideImage) {
    backCardImageSrc = primaryBackSideImage;
  }

  const replacePrimaryInsurance = () => {
    setShowForm(true);

    setSecondaryFrontSideImage(null);
    setSecondaryBackSideImage(null);
    setSecondaryInsurance(false);
    setFrontSideImage(null);
    setBackSideImage(null);
    setPrimaryInsurance(false);
    setPrimaryFrontSideImage(null);
    setPrimaryBackSideImage(null);
    setSelectedProvider('');
    setSelectedProviderDescription('');
    reset({
      groupNo: '',
      insuranceCarrier: '',
      patientRelation: '',
      planId: '',
      subscriberDob: '',
      subscriberName: '',
    });
  };

  return (
    <Container component='main' maxWidth={launchFromPatientSummery ? 'md' : 'sm'}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {loading && <CircularProgress color='primary' />}
        {!showExplanation && !payOutOfPocket && !launchFromPatientSummery ? (
          <Grid item xl={12}>
            <Alert
              severity='info'
              sx={{
                fontSize: '13px',
              }}
            >
              Please select your insurance from the dropdown (PPO plans only, no HMO or Medicaid).
              <Stack spacing={1}>
                <BulletItem>
                  If your insurance is not listed, click{' '}
                  <Link
                    href='https://www.mindweal.com/insurance/'
                    target='_blank'
                    rel='noopener'
                    underline='hover'
                    sx={{ fontSize: '13px' }}
                  >
                    here
                  </Link>{' '}
                  to view our full list of accepted plans.
                </BulletItem>

                <BulletItem>
                  If your insurance is accepted but does not appear in the dropdown, please select “Other”.
                </BulletItem>

                <BulletItem>If your insurance is not accepted, please turn on “Pay Out of Pocket”.</BulletItem>
              </Stack>{' '}
            </Alert>
          </Grid>
        ) : null}
        <Grid container spacing={2}>
          {launchFromPatientSummery && (
            <Grid item xs={12}>
              <Typography variant='h2' color='#475467'>
                Insurance
              </Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name='payout'
                  onChange={(e: any) => handlePayOutOfPocketChange(e)}
                  checked={payOutOfPocket}
                  disabled={loading || !canEdit}
                />
              }
              label='Pay out of pocket'
            />
          </Grid>
          {(showExplanation || payOutOfPocket) && (
            <>
              <Alert severity='info' sx={{ fontSize: '12px', lineHeight: 1, marginBottom: '1rem' }}>
                <Link href='https://www.mindweal.com/self-pay/' target='_blank' variant='body1'>
                  View our transparent out-of-pocket pricing
                </Link>
              </Alert>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid #E4E7EC',
                  backgroundColor: '#F9FAFB',
                  mb: 1,
                }}
              >
                <Stack spacing={1.5}>
                  <Typography fontWeight={600} fontSize='14px'>
                    Payment Method Required
                  </Typography>

                  <Typography fontSize='13px'>A valid payment method is required to schedule appointments.</Typography>

                  <Typography sx={{ fontSize: '13px', mb: 1 }}>
                    <Box component='span' sx={{ fontWeight: 600 }}>
                      Payment Method Status:
                    </Box>
                    <Box
                      component='span'
                      sx={{
                        color: creditCardStatus === 'Payment method on file' ? 'green' : '#F04438',
                        fontWeight: 'bold', // ✅ makes text bold
                      }}
                    >
                      {'  '} {creditCardStatus}
                    </Box>
                  </Typography>
                  {!isAdmin ? (
                    <>
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant='contained'
                          size='small'
                          onClick={() => setShowPaymentConfirm(true)}
                          sx={{
                            mt: '20px',
                            fontSize: '11px',
                          }}
                        >
                          {creditCardStatus === 'Payment method on file' ? 'UPDATE' : 'ADD'} PAYMENT METHOD
                        </Button>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1 }}>
                        <LockOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', flexShrink: 0, mt: '2px' }} />
                        <Typography variant='caption' color='text.secondary' sx={{ lineHeight: 1.4 }}>
                          Payments are securely processed by Stripe. We do not store your card details.
                        </Typography>
                      </Box>
                    </>
                  ) : null}
                </Stack>
              </Box>
            </>
          )}
        </Grid>
        {activeStep === 0 && showForm && !payOutOfPocket ? (
          <Box component='form' noValidate sx={{ mt: 1 }} onSubmit={handleSubmit((formData) => onSubmitForm(formData))}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                {secondaryInsurance && (
                  <Typography variant='h2' color='#475467'>
                    Add Secondary Insurance
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor='insuranceCarrier'>
                  {canEdit ? 'Your Insurance Carrier*' : 'Your Insurance Carrier'}
                </InputLabel>

                {canEdit && insuranceProviders?.length > 0 ? (
                  <TextField
                    select
                    fullWidth
                    id='insuranceCarrier'
                    autoComplete='insuranceCarrier'
                    // eslint-disable-next-line max-len
                    value={watch('insuranceCarrier') ? watch('insuranceCarrier') : ''}
                    {...register('insuranceCarrier')}
                    error={!!errors.insuranceCarrier}
                    helperText={errors?.insuranceCarrier?.message}
                    onChange={(e) => {
                      register('insuranceCarrier').onChange(e);
                      setSelectedProvider(e.target.value);
                    }}
                  >
                    {insuranceProviders?.map((provider: any) => (
                      <MenuItem key={provider.name} value={provider.name}>
                        {provider.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField fullWidth id='insuranceCarrier' value={watch('insuranceCarrier')} disabled />
                )}
              </Grid>
              {selectedProviderDescription && (
                <Grid item xs={12}>
                  <Alert severity='info' sx={{ fontSize: '12px' }}>
                    <Box
                      sx={{
                        '& b': { fontWeight: 600 },
                        lineHeight: 1.6,
                        fontSize: '12px',
                      }}
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedProviderDescription) }}
                    />
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <InputLabel htmlFor='planId'>Member ID*</InputLabel>
                <TextField
                  required
                  fullWidth
                  placeholder='Enter Your Member Id'
                  id='planId'
                  type='text'
                  autoComplete='planId'
                  value={watch('planId') ? watch('planId') : ''}
                  {...register('planId')}
                  error={!!errors.planId}
                  helperText={canEdit ? errors?.planId?.message : ''}
                  inputProps={{ maxLength: 255 }}
                  disabled={!canEdit}
                />
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor='groupNo'>{canEdit ? 'Group No*' : 'Group No'}</InputLabel>
                <TextField
                  required
                  fullWidth
                  placeholder='Enter Insurance Group No.'
                  id='groupNo'
                  type='text'
                  autoComplete='groupNo'
                  value={watch('groupNo') ? watch('groupNo') : ''}
                  {...register('groupNo')}
                  error={!!errors.groupNo}
                  helperText={canEdit ? errors?.groupNo?.message : ''}
                  disabled={!canEdit}
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='subscriberName'>
                  {canEdit ? 'Name of Subscriber*' : 'Name of Subscriber'}
                </InputLabel>
                <TextField
                  required
                  fullWidth
                  placeholder='Enter name of subscriber'
                  id='subscriberName'
                  type='text'
                  autoComplete='subscriberName'
                  value={watch('subscriberName') ? watch('subscriberName') : ''}
                  {...register('subscriberName')}
                  error={!!errors.subscriberName}
                  helperText={canEdit ? errors?.subscriberName?.message : ''}
                  disabled={!canEdit}
                  inputProps={{
                    maxLength: 255,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor='subscriberbirtday'>
                  {canEdit ? 'Birthdate of Subscriber*' : 'Birthdate of Subscriber'}
                </InputLabel>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    sx={{ width: '100%' }}
                    disableFuture
                    value={watch('subscriberDob') ? dayjs(watch('subscriberDob')) : null}
                    onChange={(e) => handleDateChange(e)}
                    disabled={!canEdit}
                    slotProps={{
                      textField: {
                        error: !!errors.subscriberDob,
                        helperText: errors?.subscriberDob?.message,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <InputLabel htmlFor='patientRelation '>
                  {canEdit ? 'Subscriber’s relationship to patient*' : 'Subscriber’s relationship to patient'}
                </InputLabel>
                <TextField
                  fullWidth
                  placeholder='Select relationship to patient'
                  id='patientRelation '
                  type='text'
                  value={watch('patientRelation') ? watch('patientRelation') : ''}
                  {...register('patientRelation')}
                  error={!!errors.patientRelation}
                  helperText={canEdit ? errors?.patientRelation?.message : ''}
                  disabled={!canEdit}
                ></TextField>
              </Grid>

              {canEdit && (
                <>
                  <Grid item xs={12}>
                    <Typography variant='h2'>Upload Insurance Card Image</Typography>
                  </Grid>
                  <Grid item xs={12} sx={{ p: '1rem' }}>
                    <InputLabel htmlFor='frontSide'>Front Side</InputLabel>
                    {frontCardImageSrc && (
                      <img
                        src={`data:image/png;base64, ${frontCardImageSrc}`}
                        alt='front side'
                        style={cardImageStyle}
                      />
                    )}
                    <FileUploader
                      onFileUpload={handleFileUpload}
                      uploadProgress={frontCardUploadProgress}
                      side='front'
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ p: '1rem' }}>
                    <InputLabel htmlFor='frontSide'>Back Side</InputLabel>
                    {backCardImageSrc && (
                      <img src={`data:image/png;base64, ${backCardImageSrc}`} alt='back side' style={cardImageStyle} />
                    )}

                    <FileUploader onFileUpload={handleFileUpload} uploadProgress={backCardUploadProgress} side='back' />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={8} display={'flex'} sx={{ justifyContent: { xs: 'center', sm: 'left' } }}>
                {primaryInsuranceData !== null ? (
                  <Button
                    variant='text'
                    sx={{ mt: 2, mb: 2 }}
                    onClick={() => {
                      setShowForm(false);
                    }}
                  >
                    Back to Insurance Details
                  </Button>
                ) : null}
              </Grid>
              <Restricted permission={permissions.EDIT_PROFILE}>
                <Grid container justifyContent='center'>
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center', // ✅ center horizontally
                    }}
                  >
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      sx={{
                        minWidth: 140,
                        mt: 2,
                        mb: 2,
                      }}
                    >
                      Save
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Restricted>
            </Grid>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Grid container spacing={2}>
              {primaryInsuranceData !== null && !payOutOfPocket && !showExplanation && canEdit ? (
                <Grid item xs={12} sm={6}>
                  <PrimaryCard
                    editInsurance={editPrimaryInsurance}
                    insurancetype='Primary'
                    data={primaryInsuranceData}
                    fromadmin={false}
                  />

                  {canEdit && (
                    <Button
                      variant='text'
                      onClick={() => {
                        replacePrimaryInsurance();
                      }}
                    >
                      Replace
                    </Button>
                  )}
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6}>
                {secondaryInsuranceData !== null && !payOutOfPocket && !showExplanation && canEdit && (
                  <>
                    <PrimaryCard
                      editInsurance={editSecondaryInsurance}
                      insurancetype='Secondary'
                      data={secondaryInsuranceData}
                      fromadmin={false}
                    />
                    <Button
                      variant='text'
                      sx={{ color: '#F04438' }}
                      disabled={disableDelete}
                      onClick={() => {
                        setDeleteType('SECONDARY');
                        setShowConfirm(true);
                      }}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </Grid>
              {primaryInsuranceData !== null && secondaryInsuranceData === null && !payOutOfPocket && (
                <Restricted permission={permissions.EDIT_PROFILE}>
                  {canEdit ? (
                    <Button
                      variant='text'
                      onClick={() => {
                        setShowForm(true);
                        setPrimaryInsurance(false);
                        setSecondaryInsurance(true);
                        setSelectedProviderDescription('');
                        setSelectedProvider('');
                      }}
                    >
                      Add Secondary Insurance
                    </Button>
                  ) : null}
                </Restricted>
              )}
            </Grid>
          </Box>
        )}
        {displayInsuranceMessage && !payOutOfPocket && !showExplanation && activeStep === 0 && (
          <Grid item xs={12}>
            <Alert severity='error' sx={{ fontSize: '12px' }}>
              This insurance is not accepted in the current state, please update your insurance or opt to pay out of
              pocket to continue MindWeal services
            </Alert>
          </Grid>
        )}
      </Box>
      <StyledDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        maxWidth='xs' // ✅ smaller width
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          {' '}
          {/* ✅ reduce padding */}
          <Grid container spacing={1.5} textAlign='center'>
            {/* Title */}
            <Grid item xs={12}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                Delete Secondary Insurance
              </Typography>
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '13px', px: 1 }}>
                Are you sure you want to delete your secondary insurance?
              </Typography>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1, // ✅ smaller gap
                  justifyContent: 'center',
                  flexDirection: { xs: 'column', sm: 'row' },
                  mt: 1, // ✅ reduce margin
                }}
              >
                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    minWidth: 100, // ✅ smaller button
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '16px',
                    backgroundColor: '#F04438',
                    '&:hover': { backgroundColor: '#D7372B' },
                  }}
                  onClick={() => handleDeleteInsurance()}
                >
                  Delete
                </Button>

                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    minWidth: 100,
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '16px',
                  }}
                  onClick={() => setShowConfirm(false)}
                >
                  Cancel
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>

      <StyledDialog
        open={showPaymentConfirm}
        onClose={() => setShowPaymentConfirm(false)}
        maxWidth='xs' // ✅ smaller width
        fullWidth
      >
        <DialogContent sx={{ p: 2 }}>
          {' '}
          {/* ✅ reduce padding */}
          <Grid container spacing={1.5} textAlign='center'>
            {/* Title */}
            <Grid item xs={12}>
              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                {creditCardStatus === 'Payment method on file' ? 'Update Payment Method' : 'Add Payment Method'}
              </Typography>
            </Grid>

            {/* Message */}
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '13px', px: 1 }}>
                {creditCardStatus === 'Payment method on file'
                  ? 'Click OK to update your payment method securely on Stripe. When you return, it will be updated. If not, tap Refresh.'
                  : 'Click OK to add your payment method securely on Stripe. When you return, it will be updated. If not, tap Refresh.'}
              </Typography>
            </Grid>

            {/* Button */}
            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 1,
                }}
              >
                <Button
                  variant='contained'
                  size='small'
                  sx={{
                    minWidth: 90, // ✅ smaller button
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderRadius: '16px',
                    textTransform: 'none',
                  }}
                  onClick={() => redirectPaymentMethod()}
                >
                  OK
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
      </StyledDialog>
    </Container>
  );
};

export default InsuranceDetails;
